import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Gift, Copy, Check, Users, Clock, Loader2, Sparkles, Linkedin, Twitter, Facebook, MessageCircle, Send, Mail, Share2, Megaphone } from "lucide-react";

export default function Referrals() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [adminEnabled, setAdminEnabled] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/referrals/me")
      .then(({ data }) => setStats(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load referrals."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (user?.is_admin) {
      api.get("/admin/referral-config").then(({ data }) => setAdminEnabled(data.enabled)).catch(() => {});
    }
  }, [user]);

  const copyLink = async () => {
    if (!stats?.link) return;
    try {
      await navigator.clipboard.writeText(stats.link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy manually.");
    }
  };

  const link = stats?.link || "";
  const shareMsg = `I've been using Jobsmith to tailor my CV and cover letters to any job in seconds — it's genuinely helped my applications stand out. Get a free 7-day trial with my link: ${link}`;
  const statusCaption = `Job hunting? Jobsmith tailors your CV & cover letter to any job in seconds. Free 7-day trial 👉 ${link}`;
  const enc = encodeURIComponent;

  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const shareTargets = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "text-green-600",
      onClick: () => openShare(`https://wa.me/?text=${enc(shareMsg)}`) },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-[#0a66c2]",
      onClick: () => openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(link)}`) },
    { id: "twitter", label: "X / Twitter", icon: Twitter, color: "text-foreground",
      onClick: () => openShare(`https://twitter.com/intent/tweet?text=${enc(shareMsg)}`) },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "text-[#1877f2]",
      onClick: () => openShare(`https://www.facebook.com/sharer/sharer.php?u=${enc(link)}&quote=${enc(shareMsg)}`) },
    { id: "telegram", label: "Telegram", icon: Send, color: "text-[#229ED9]",
      onClick: () => openShare(`https://t.me/share/url?url=${enc(link)}&text=${enc(shareMsg)}`) },
    { id: "email", label: "Email", icon: Mail, color: "text-foreground",
      onClick: () => openShare(`mailto:?subject=${enc("Try Jobsmith — free 7-day trial")}&body=${enc(shareMsg)}`) },
  ];

  const copyStatusCaption = async () => {
    try {
      await navigator.clipboard.writeText(statusCaption);
      toast.success("Status caption copied — paste it as your WhatsApp status!");
    } catch {
      toast.error("Couldn't copy — copy the caption manually.");
    }
  };

  const deviceShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Jobsmith", text: shareMsg, url: link });
      } catch { logger.debug("native share cancelled by user"); }
    } else {
      copyStatusCaption();
      toast.info("Your browser can't open the share sheet — the caption is copied instead.");
    }
  };

  const toggleEngine = async (val) => {
    setSavingToggle(true);
    try {
      const { data } = await api.put("/admin/referral-config", { enabled: val });
      setAdminEnabled(data.enabled);
      toast.success(`Referral engine ${data.enabled ? "enabled" : "disabled"}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update.");
    } finally {
      setSavingToggle(false);
    }
  };

  if (loading) return <div className="py-24 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" /></div>;

  const disabled = stats && !stats.enabled;
  const progress = stats ? Math.min(100, Math.round(((stats.qualified % stats.threshold) / stats.threshold) * 100)) : 0;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="referrals-page">
      <div>
        <div className="flex items-center gap-2">
          <Gift className="h-6 w-6 text-gold" strokeWidth={1.75} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Refer friends, earn free weeks</h1>
        </div>
        <p className="text-muted-foreground mt-2 text-sm">
          Invite friends with your link. For every {stats?.threshold ?? 3} who sign up and verify their email, you earn {stats?.bonus_days ?? 7} bonus days of Jobsmith — free.
        </p>
      </div>

      {user?.is_admin && adminEnabled !== null && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4 flex items-center justify-between" data-testid="admin-referral-toggle">
          <div>
            <p className="text-sm font-semibold">Admin — Referral engine</p>
            <p className="text-xs text-muted-foreground">Turn the whole referral program on or off for all users.</p>
          </div>
          <Switch checked={adminEnabled} disabled={savingToggle} onCheckedChange={toggleEngine} data-testid="referral-engine-switch" />
        </div>
      )}

      {disabled ? (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-8 text-center" data-testid="referrals-disabled">
          <p className="text-sm text-muted-foreground">The referral program is currently unavailable. Please check back soon.</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6" data-testid="referral-link-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your referral link</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input readOnly value={stats?.link || ""} className="h-11 font-mono text-sm" data-testid="referral-link-input" />
              <Button onClick={copyLink} className="h-11 shrink-0 active:scale-[0.98] transition-transform" data-testid="copy-referral-link">
                {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Your code: <span className="font-mono font-semibold text-foreground">{stats?.code}</span></p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-6" data-testid="share-card">
            <div className="flex items-center gap-2 mb-1">
              <Share2 className="h-5 w-5 text-accent" strokeWidth={1.75} />
              <h2 className="font-heading text-lg font-bold">Invite everyone — share anywhere</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              There's no limit on invites. Share with as many people as you like — you earn {stats?.bonus_days ?? 7} free days for every {stats?.threshold ?? 3} who sign up and verify their email.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="share-buttons">
              {shareTargets.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={t.onClick}
                    data-testid={`share-${t.id}`}
                    className="flex items-center justify-center gap-2 h-11 rounded-lg border border-zinc-200 bg-white text-sm font-medium hover:bg-zinc-50 hover:border-zinc-300 active:scale-[0.98] transition-all"
                  >
                    <Icon className={`h-4 w-4 ${t.color}`} strokeWidth={1.75} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 pt-5 border-t border-zinc-100 flex flex-col sm:flex-row gap-2.5">
              <Button onClick={deviceShare} className="h-11 flex-1 active:scale-[0.98] transition-transform" data-testid="share-device">
                <Share2 className="h-4 w-4 mr-1.5" /> Share via device
              </Button>
              <Button onClick={copyStatusCaption} variant="outline" className="h-11 flex-1 active:scale-[0.98] transition-transform" data-testid="share-whatsapp-status">
                <Megaphone className="h-4 w-4 mr-1.5" /> Copy WhatsApp status
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Tip: tap <strong>Share via device</strong> on your phone to post your link straight to your WhatsApp status, Instagram or anywhere else.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="stat-qualified">
              <Users className="h-5 w-5 text-green-600 mb-2" strokeWidth={1.75} />
              <div className="font-heading text-3xl font-bold">{stats?.qualified ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Verified referrals</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="stat-pending">
              <Clock className="h-5 w-5 text-amber-500 mb-2" strokeWidth={1.75} />
              <div className="font-heading text-3xl font-bold">{stats?.pending ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting verification</p>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="stat-bonus">
              <Sparkles className="h-5 w-5 text-gold mb-2" strokeWidth={1.75} />
              <div className="font-heading text-3xl font-bold">{(stats?.bonus_trials_granted ?? 0) * (stats?.bonus_days ?? 7)}</div>
              <p className="text-xs text-muted-foreground mt-1">Bonus days earned</p>
            </div>
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="referral-progress">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Progress to your next free week</p>
              <p className="text-sm text-muted-foreground">{stats?.needed_for_next} more to go</p>
            </div>
            <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
              <div className="h-full bg-gold rounded-full transition-[width] duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
